/**
 * Profile Settings Modal
 * Displays user profile information and account management options
 */

import { useState } from 'react';
import FocusTrap from 'focus-trap-react';
import { X, User, Trash2, AlertTriangle } from 'lucide-react';
import { useAuth } from '@/context';
import { useToast } from '@/components/ui';
import { signOut } from '@/services/auth';
import styles from './ProfileSettingsModal.module.css';
import type { ProfileSettingsModalProps } from './ProfileSettingsModal.types';

export function ProfileSettingsModal({ isOpen, onClose }: ProfileSettingsModalProps) {
  const { user, session } = useAuth();
  const { showSuccess, showError } = useToast();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState('');
  const [deleteReason, setDeleteReason] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  if (!isOpen || !user) return null;

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isDeleting) {
      onClose();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape' && !isDeleting) {
      onClose();
    }
  };

  const handleDeleteAccount = async () => {
    if (!session) {
      showError('You must be signed in to delete your account');
      return;
    }

    if (confirmEmail !== user.email) {
      showError('Email does not match. Please enter your email correctly.');
      return;
    }

    try {
      setIsDeleting(true);

      const response = await fetch('/api/account/delete', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          confirmEmail,
          reason: deleteReason || undefined,
        }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(data.error || 'Failed to delete account');
      }

      showSuccess('Your account has been deleted. Goodbye!');

      // Sign out and close modal
      await signOut();
      onClose();
    } catch (error) {
      console.error('Failed to delete account:', error);
      showError(error instanceof Error ? error.message : 'Failed to delete account');
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setConfirmEmail('');
    setDeleteReason('');
  };

  return (
    <FocusTrap
      focusTrapOptions={{
        allowOutsideClick: true,
        escapeDeactivates: !isDeleting,
        returnFocusOnDeactivate: true,
      }}
    >
      <div
        className={styles.overlay}
        onClick={handleOverlayClick}
        onKeyDown={handleKeyDown}
        role="dialog"
        aria-modal="true"
        aria-labelledby="profile-settings-title"
      >
        <div className={styles.modal}>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Close profile settings"
            disabled={isDeleting}
          >
            <X size={20} aria-hidden="true" />
          </button>

          <h2 id="profile-settings-title" className={styles.title}>
            Profile Settings
          </h2>

          {/* Account Info Section */}
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>Account Information</h3>
            <div className={styles.infoCard}>
              <div className={styles.avatar}>
                <User size={32} aria-hidden="true" />
              </div>
              <div className={styles.infoDetails}>
                <span className={styles.infoLabel}>Email</span>
                <span className={styles.infoValue}>{user.email}</span>
              </div>
            </div>
          </section>

          {/* Danger Zone */}
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>Danger Zone</h3>

            {!showDeleteConfirm ? (
              <div className={styles.dangerCard}>
                <div className={styles.dangerInfo}>
                  <Trash2 size={20} className={styles.dangerIcon} aria-hidden="true" />
                  <div>
                    <span className={styles.dangerTitle}>Delete Account</span>
                    <span className={styles.dangerDescription}>
                      Permanently delete your account and all associated data
                    </span>
                  </div>
                </div>
                <button
                  type="button"
                  className={styles.dangerButton}
                  onClick={() => setShowDeleteConfirm(true)}
                >
                  Delete Account
                </button>
              </div>
            ) : (
              <div className={styles.deleteConfirm}>
                <div className={styles.warningBanner}>
                  <AlertTriangle size={20} aria-hidden="true" />
                  <span>
                    This action cannot be undone. All your data will be permanently deleted.
                  </span>
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="confirm-email" className={styles.label}>
                    Type your email to confirm: <strong>{user.email}</strong>
                  </label>
                  <input
                    id="confirm-email"
                    type="email"
                    className={styles.input}
                    value={confirmEmail}
                    onChange={(e) => setConfirmEmail(e.target.value)}
                    placeholder="Enter your email"
                    disabled={isDeleting}
                    autoComplete="off"
                  />
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="delete-reason" className={styles.label}>
                    Reason for leaving (optional)
                  </label>
                  <textarea
                    id="delete-reason"
                    className={styles.textarea}
                    value={deleteReason}
                    onChange={(e) => setDeleteReason(e.target.value)}
                    placeholder="Help us improve by sharing why you're leaving..."
                    rows={3}
                    disabled={isDeleting}
                  />
                </div>

                <div className={styles.confirmActions}>
                  <button
                    type="button"
                    className={styles.cancelButton}
                    onClick={handleCancelDelete}
                    disabled={isDeleting}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className={styles.confirmDeleteButton}
                    onClick={handleDeleteAccount}
                    disabled={isDeleting || confirmEmail !== user.email}
                  >
                    {isDeleting ? 'Deleting...' : 'Permanently Delete'}
                  </button>
                </div>
              </div>
            )}
          </section>
        </div>
      </div>
    </FocusTrap>
  );
}
